<template>
  <div ref="buynow">
    <transition name="fade">
      <div
        v-if="showStickyBuyNow && !(deal.is_info || inactiveIndefinitely)"
        class="d-flex align-items-center justify-content-around"
      >
        <div
          v-if="deal.default_price"
          class="px-2 text-center"
        >
          <div
            class="pdp-buynow-price-sticky p-0 m-0"
          >
            <span v-if="selectedPlan">{{ selectedPriceText }}</span>
            <span v-else>{{ defaultPriceText }}</span>
            <span
              v-if="selectedPlan && selectedPlan.original_price"
              class="pdp-buynow-old-price"
            >
              {{ selectedPlan.original_price | currencyWithOptionalDecimal }}
            </span>
          </div>
        </div>
        <!-- an: make button full width in case no default_price usign flex-grow -->
        <div
          class="px-2"
          :class="deal.default_price ? 'flex-shrink-0' : 'flex-grow-1'"
        >
          <!-- if deal has clickthrough, use a link button to send them to that URL -->
          <a
            v-if="clickthroughUrl"
            class="btn btn-primary pdp-buynow-btn-mobile"
            :href="clickthroughUrl"
          >
            {{ buyButtonText }}
          </a>
          <!-- jw: if the user doesn't have plus link them to learn more about it. -->
          <a
            v-else-if="notPlusAndEarlyAccessOrLastCall"
            :href="`/plus/?ref=plus-page-${deal.slug}`"
            class="btn btn-primary pdp-buynow-plus-mobile"
          >
            {{ buyButtonText }}
          </a>
          <!-- jw: if the user does have plus then allow them to buy it like normal. -->
          <button
            v-else
            class="btn btn-primary pdp-buynow-btn-mobile"
            :disabled="disabledBuyButton"
            @click="postSignupOrAddToCart()"
            @keydown.enter="postSignupOrAddToCart()"
          >
            {{ buyButtonText }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

import { LISTING_TYPES_WITH_LOGO } from '~/constants/listing-types';
import SubmitEmailMixin from '../../mixins/submit-email';
import {
  CART_LOGIN_REDIRECT_EXPERIMENT_NAME,
  COMING_SOON_BANNER_TEXT,
} from '../../constants/global';

export default {
  LISTING_TYPES_WITH_LOGO,
  mixins: [SubmitEmailMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    buyButton: {
      type: Object,
      required: true,
    },
    plans: {
      type: Array,
      required: false,
      default: () => [],
    },
    // TODO temp user object to be replaced by this.$store.state.session.user
    user: {
      type: Object,
      required: true,
    },
    bannerDetails: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isDisabled: false,
      showStickyBuyNow: false,
      mobileMatchMedia: null,
      storyObserver: null,
    };
  },
  computed: {
    selectedPlan() {
      return this.$store.state.selectedPlan?.plan;
    },
    buyButtonText() {
      if (this.buyButton.is_draft) {
        return 'Buy now (Draft)';
      }

      // is_coming_soon will be true if the current user does not have early access, so we want
      //     to handle these cases first since early access behavior should trump coming soon.
      //
      // note: buyButton.is_coming_soon behaves slightly differently than our bannerDetails
      // coming soon, so we're including the banner details check here to match buy button text
      // to our pdp stage banner as a temporary fix
      if (!this.earlyAccessOrLastCall
        && (this.bannerDetails?.text === COMING_SOON_BANNER_TEXT || this.buyButton.is_coming_soon)
      ) {
        return 'Coming soon';
      }

      if (this.buyButton.is_ended) {
        return 'Sold out';
      }

      if (this.buyButton.is_get_now) {
        return this.deal.is_free ? 'Get now for FREE' : 'Get now';
      }

      return this.deal.is_free ? 'Get now for FREE' : 'Buy now';
    },
    clickthroughUrl() {
      return this.deal.clickthrough_url;
    },
    disabledBuyButton() {
      return this.disabledState || this.buyButton.is_coming_soon;
    },
    defaultPriceText() {
      const formattedPrice = this
        .$options
        .filters
        .currencyWithOptionalDecimal(this.deal.default_price);

      if (this.deal.plan_stack_count > 1) {
        return `Starting at ${formattedPrice}`;
      }

      return formattedPrice;
    },
    selectedPriceText() {
      if (this.selectedPlan) {
        return this.getPlanDisplayText(this.selectedPlan);
      }

      return this.defaultPriceText;
    },
    hasPlans() {
      return this.plans && this.plans.length > 1;
    },
    disabledState() {
      if (this.buyButton.is_ended) {
        return true;
      }
      if (this.hasPlans) {
        return !this.selectedPlan;
      }
      return this.isDisabled;
    },
    stageDetails() {
      return this.deal.stage_details || {};
    },
    inactiveIndefinitely() {
      return this.stageDetails.is_inactive_indefinitely;
    },
    earlyAccessOrLastCall() {
      return this.buyButton.is_early_access || this.buyButton.is_last_call;
    },
    notPlusAndEarlyAccessOrLastCall() {
      return !this.user.hasPlus && this.earlyAccessOrLastCall;
    },
    redirectAnonUserToLogin() {
      return this.$store.getters['experiments/getExperimentVariant'](CART_LOGIN_REDIRECT_EXPERIMENT_NAME) === 'enabled';
    },
    currentUser() {
      return this.$store.state.user;
    },
  },
  mounted() {
    // this is roughly the width needed to not make our sticky nav 2 columns
    this.mobileMatchMedia = window.matchMedia('(min-width: 1200px)');

    this.mobileMatchMedia.addListener(this.toggleObservers);

    this.toggleObservers(this.mobileMatchMedia);
  },
  beforeDestroy() {
    this.mobileMatchMedia.removeListener(this.toggleObservers);
    this.destroyObservers();
  },
  methods: {
    // seems silly, but let's centralize this common format into a helper function.
    getPlanDisplayText(plan) {
      return `${this.$options.filters.currencyWithOptionalDecimal(plan.price)}`;
    },
    pluralizeCodeText(code) {
      return `${code} Code${code > 1 ? 's' : ''}`;
    },
    toggleObservers(event) {
      if (event.matches) {
        this.registerStoryObserver();
      } else {
        // remove any observer
        this.showStickyBuyNow = false;
        this.destroyObservers();
      }
    },
    registerStoryObserver() {
      // this includes two sets of buy buttons:
      // 1. our pricing card
      // 2. our pricing table buy buttons
      // so, let's only display our sticky nav buy button once these two are no longer in view
      const storyContainer = document.querySelector('.pdp-story-container');

      this.storyObserver = new IntersectionObserver((entries) => {
        const [storyContainerEntry] = entries;
        this.showStickyBuyNow = !storyContainerEntry.isIntersecting;
      });

      this.storyObserver.observe(storyContainer);
    },
    destroyObservers() {
      if (this.storyObserver) {
        this.storyObserver.disconnect();
        this.storyObserver = null;
      }
    },
    async postSignupOrAddToCart() {
      this.postAddToCart();
    },
    postAddToCart() {
      this.isDisabled = true;
      const data = new FormData();
      data.append('id', this.deal.id);
      if (this.selectedPlan) {
        data.append('plan_id', this.selectedPlan.id);
        data.append('quantity', this.selectedPlan.codes);
      } else if (this.deal.default_plan) {
        data.append('plan_id', this.deal.default_plan.id);
      }
      if (this.deal.is_free) {
        const quantity = (this.deal.use_licensing) ? 1 : this.deal.default_plan.codes;
        data.append('quantity', quantity);
      }
      axios.post('/cart/add/', data, this.$_formHeaders())
        .then(() => {
          this.isDisabled = false;
          if (window) {
            // if user is not logged in, and experiment is enabled
            if (!this.currentUser?.isAuthenticated && this.redirectAnonUserToLogin) {
              window.location = '/accounts/login/?next=/cart/';
            } else {
              window.location = '/cart/';
            }
          }
        })
        .catch(() => {
          this.$toastr.e('Something has gone wrong with this request. Please try again.');
          this.isDisabled = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~/styles/dorado/themes/dorado';
  @import '../../styles/abstracts/mixins';
  @import '../../styles/utils/breakpoints';

  .pdp-buynow-mobile-head {
    background: #fff;
    bottom: 0;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    left: 0;
    padding: 10px 5px;
    position: fixed;
    right: 0;
    z-index: 9000;
  }

  /* stylelint-disable selector-no-qualifying-type */
  span.pdp-buynow-old-price {
    text-decoration: line-through;
    color: #8e9baa;
    font-size: 16px;
    line-height: 21px;
  }

  span.pdp-buynow-mobile-head,
  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-size: 16px;
    line-height: 24px;
  }

  .pdp-buynow-plus-mobile,
  a.pdp-buynow-btn-mobile,
  button.pdp-buynow-btn-mobile {
    font-weight: 700;
    padding: 8px 24px;
    border-radius: 40px;
  }

  // an: Limit buttons width when having default_price
  .flex-shrink-0 .pdp-buynow-plus-mobile,
  .flex-shrink-0 .pdp-buynow-btn-mobile {
    max-width: 195px;
    white-space: inherit;
  }

  // an: Make buttons full width when not having default_price
  .flex-grow-1 .pdp-buynow-plus-mobile,
  .flex-grow-1 .pdp-buynow-btn-mobile {
    width: 100%;
  }

  .deal-unavailable-sticky {
    font-size: 16px;
    line-height: 21px;
  }

  div.pdp-buynow-price-sticky {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }
  /* stylelint-enable selector-no-qualifying-type */

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  /* potential temp styling below */
  .btn-primary {
    border: 1px solid #ffbc00;
    background-color: #ffbc00;
    color: #272727;
    padding: 12px 32px;
    font-size: 16px;
  }

  .btn-primary:hover,
  .btn-primary:disabled {
    border-color: #ffcb33;
    background-color: #ffcb33;
    color: #272727;
  }

  .btn-primary:active,
  .btn-primary:focus {
    border-color: #ffcb33 !important;
    background-color: #ffcb33 !important;
    color: #272727 !important;
  }

  .success {
    background: #0a840a;
    padding: 16px 20px;
    color: #fff;
    display: inline-flex;
    align-items: center;
  }
</style>
